((root) => {
  'use strict';
  if (root.KMCreateCartState) return;

  function createCartState({fetchImpl, requestTimeoutMs = 12000}) {
    let version = 0;
    const snapshots = new Map();
    const reads = new Map();
    const markupReads = new Map();
    const writes = new Set();
    // 排队时立即占用写锁；读请求必须等队列全部完成，失败不能阻断后续写入。
    let writeQueue = Promise.resolve();
    const origin = root.location?.origin || 'https://cart.local';
    async function timedRequest(url, init, consume, implementation = fetchImpl) {
      const controller = new AbortController();
      const signal = init.signal;
      const abort = () => controller.abort(signal.reason);
      if (signal?.aborted) abort();
      else signal?.addEventListener('abort', abort, {once: true});
      let timer;
      const timeout = new Promise((resolve, reject) => {
        timer = setTimeout(() => {
          const error = new Error('Cart verification timed out. Please retry before checkout.');
          error.code = 'KM_CART_TIMEOUT';
          reject(error);
          controller.abort(error);
        }, requestTimeoutMs);
      });
      try {
        return await Promise.race([timeout, (async () => {
          const response = await implementation(url, {...init, signal: controller.signal});
          return consume(response);
        })()]);
      } finally {
        clearTimeout(timer);
        signal?.removeEventListener('abort', abort);
      }
    }
    const keyFor = url => {
      const parsed = new URL(url, origin);
      parsed.searchParams.delete('t');
      parsed.searchParams.sort();
      return parsed.href;
    };

    function invalidate() {
      version++;
      snapshots.clear();
      reads.clear();
      markupReads.clear();
    }

    async function waitForWrites() {
      while (writes.size) await Promise.all([...writes]);
    }

    async function read(url, {fresh = false} = {}) {
      await waitForWrites();
      const key = keyFor(url);
      const pending = reads.get(key);
      if (pending?.version === version) return pending.promise;
      const cached = snapshots.get(key);
      if (!fresh && cached?.version === version) return cached.cart;
      const record = {version};
      record.promise = (async () => {
        const cart = await timedRequest(url, {credentials: 'same-origin', cache: 'no-store',
          headers: {Accept: 'application/json'}}, async response => {
          if (!response.ok) throw new Error('Unable to load cart. Please try again.');
          return response.json();
        });
        if (!Array.isArray(cart?.items) || !Number.isFinite(Number(cart.total_price))) {
          throw new Error('Unable to verify cart. Please try again.');
        }
        // A response belonging to an earlier mutation must never become current.
        if (record.version !== version) return read(url, {fresh});
        const fingerprint = JSON.stringify(cart);
        if (cached && cached.fingerprint !== fingerprint) invalidate();
        snapshots.set(key, {cart, fingerprint, version});
        return cart;
      })().catch(error => {
        if (record.version === version) invalidate();
        throw error;
      }).finally(() => { if (reads.get(key) === record) reads.delete(key); });
      reads.set(key, record);
      return record.promise;
    }

    async function request(url, init = {}, implementation = fetchImpl) {
      const parsed = new URL(url, origin);
      const mutation = parsed.origin === origin && (init.method || 'GET').toUpperCase() === 'POST'
        && /\/cart\/(add|change|update|clear)(\.js)?\/?$/.test(parsed.pathname);
      if (!mutation) return implementation(url, init);
      invalidate();
      let release;
      const pending = new Promise(resolve => { release = resolve; });
      writes.add(pending);
      const previous = writeQueue;
      writeQueue = pending;
      try {
        await previous;
        return await timedRequest(url, init, async response => {
          // Keep the write lock and timeout through the body download. Consumers
          // retain the original Response and can still use either text() or json().
          if (response.clone) await response.clone().arrayBuffer();
          return response;
        }, implementation);
      }
      finally {
        // Even a lost/failed response can follow a committed Shopify mutation.
        invalidate();
        writes.delete(pending);
        release();
      }
    }

    async function markup(url, {fresh = false} = {}) {
      await waitForWrites();
      const key = keyFor(url);
      const cached = markupReads.get(key);
      if (cached?.version === version && (!fresh || !cached.settled)) return cached.promise;
      const record = {version};
      record.promise = (async () => {
        const text = await timedRequest(url, {credentials: 'same-origin', cache: 'no-store'}, async response => {
          if (!response.ok) throw new Error('Unable to refresh your cart. Please try again.');
          return response.text();
        });
        record.settled = true;
        return record.version === version ? text : markup(url);
      })().catch(error => {
        if (markupReads.get(key) === record) markupReads.delete(key);
        throw error;
      });
      markupReads.set(key, record);
      return record.promise;
    }

    return {read, fetch: request, markup, invalidate, get version() { return version; }};
  }

  root.KMCreateCartState = createCartState;
  if (!root.document) return;
  root.KMCartState = createCartState({fetchImpl: root.fetch.bind(root)});
  ['cart:updated', 'cart:rendered'].forEach(name => root.document.addEventListener(name, event => {
    // Mutation event bodies have no server version and can arrive out of order.
    // Invalidate and share a GET instead of promoting them to trusted snapshots.
    if (name === 'cart:updated' || event.detail?.cart) root.KMCartState.invalidate();
  }, true));
  // Third-party adds and another tab may change Shopify without our request helpers.
  root.document.addEventListener('ajaxProduct:added', root.KMCartState.invalidate, true);
  root.document.addEventListener('drawerOpen.CartDrawer', root.KMCartState.invalidate, true);
  root.document.addEventListener('visibilitychange', root.KMCartState.invalidate, true);
  ['pageshow', 'storage'].forEach(name => root.addEventListener(name, root.KMCartState.invalidate, true));
  // 捕获阶段也能收到页面内按钮的 focus；仅窗口重新获焦才需要跨标签校验。
  root.addEventListener('focus', event => {
    if (event.target === root) root.KMCartState.invalidate();
  }, true);
})(typeof window !== 'undefined' ? window : globalThis);
